<template>
  <div class="">
    <List
      v-slot="{ entity: user }"
      :entities="publisherStore.users"
      :page="publisherStore.usersPage"
      :loading="publisherStore.isLoading"
      :updatePageNumber="updatePageNumber"
    >
      <div class="px-4 py-4 sm:px-6">
        <div class="flex items-center justify-between">
          <p class="truncate text-sm">
            {{ user.email }}
          </p>
          <div class="ml-2 flex shrink-0">
            <p
              v-if="!user.reference"
              class="truncate pr-3 text-sm text-gray-400"
            >
              ({{ t('publisher.user.invited') }})
            </p>
            <button
              class="rounded-xs focus:border-red-400 focus:ring-2 focus:ring-red-400 focus:outline-hidden"
              :aria-label="t('publisher.user.deleteTitle')"
              @click="confirmUserDelete(user)"
            >
              <TrashIcon class="h-5 w-5 text-gray-400 hover:text-red-400" />
            </button>
          </div>
        </div>
      </div>
    </List>
  </div>

  <TransitionRoot appear :show="confirmationModalOpen" as="template">
    <Dialog
      as="div"
      class="relative z-10"
      @close="() => (confirmationModalOpen = false)"
    >
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="duration-200 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50"></div>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center"
        >
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-lg transform overflow-hidden rounded-2xl bg-gray-50 p-6 text-left align-middle shadow-xl transition-all"
            >
              <div class="flex w-full gap-6">
                <div
                  class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                >
                  <ExclamationTriangleIcon
                    class="h-6 w-6 text-red-600"
                    aria-hidden="true"
                  />
                </div>
                <div class="w-full">
                  <DialogTitle
                    as="h3"
                    class="flex text-lg leading-6 font-medium text-gray-900"
                  >
                    {{ t('publisher.user.deleteTitle') }}

                    <button
                      class="ml-auto"
                      @click="() => (confirmationModalOpen = false)"
                    >
                      <XMarkIcon class="h-6 w-6 text-gray-500" />
                    </button>
                  </DialogTitle>
                  <div class="mt-2 w-fit">
                    <p class="mb-2 text-sm text-gray-500">
                      {{ t('publisher.user.deleteConfirmation') }}
                    </p>
                    <p class="text-center font-medium">
                      {{ selectedUser?.email }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  class="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-xs hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-hidden sm:ml-3 sm:w-auto sm:text-sm"
                  @click="deleteUser()"
                >
                  {{ t('publisher.user.delete') }}
                  <LoadingSpinner
                    v-if="publisherStore.isLoading"
                    class="ml-3 h-4 w-4 text-white"
                  />
                </button>
                <button
                  ref="cancelButtonRef"
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden sm:mt-0 sm:w-auto sm:text-sm"
                  @click="confirmationModalOpen = false"
                >
                  {{ t('publisher.user.cancelDelete') }}
                </button>
              </div>
              <ErrorMessages
                v-if="publisherStore.userErrors.length"
                :errors="publisherStore.userErrors"
                class="mt-4"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue'
import { usePublisherStore } from '../../store/publisherStore'
import { useI18n } from 'vue-i18n'
import { TrashIcon, XMarkIcon } from '@heroicons/vue/20/solid'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import List from '../List.vue'
import ErrorMessages from '../ErrorMessages.vue'
import LoadingSpinner from '../LoadingSpinner.vue'

const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const publisherStore = usePublisherStore()

const updatePageNumber = (number) => {
  publisherStore.usersPage.number = number
  publisherStore.loadUsers()
}

const confirmationModalOpen = ref(false)
const selectedUser = ref(null)
const confirmUserDelete = (user) => {
  selectedUser.value = user
  confirmationModalOpen.value = true
}
const deleteUser = async () => {
  await publisherStore.deleteUser(selectedUser.value)
  if (publisherStore.userErrors.length === 0) {
    confirmationModalOpen.value = false
    await nextTick()
    selectedUser.value = null
  }
}

// Clear error messages when the modal is opened/closed
watch(confirmationModalOpen, () => {
  publisherStore.userErrors = []
})
</script>
